import { useCallback, useMemo, useRef } from 'react';

import type {
  FilterModel,
  IServerSideDatasource,
  IServerSideGetRowsParams,
} from 'ag-grid-community';

import { auditApi } from '../api';
import { transformAuditLog } from './grid';
import type { TransformedLog } from './types';

export type ExternalFilters = {
  q: string;
  /** ISO timestamps (inclusive). Empty string means open-ended. */
  startDate: string;
  endDate: string;
};

type Params = {
  objectId: string | undefined;
  filters: ExternalFilters;
  /** Called once after each successful page load with the entity display name from the response. */
  onMetadata?: (entityDisplayName: string | null) => void;
};

/**
 * Builds an ag-grid server-side datasource that hits nucleus's
 * `/api/v1/AuditLog/history` endpoint. Pagination, sorting (by timestamp),
 * column filters and the toolbar's quick-search + date-range presets are all
 * pushed down to audit-api / Cosmos.
 */
export function useAuditDatasource({ objectId, filters, onMetadata }: Params) {
  // Keep a stable ref to filters so the datasource identity is stable while
  // filter values still flow into each fetch via current values.
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const getRows = useCallback(
    async (params: IServerSideGetRowsParams) => {
      if (!objectId) {
        params.success({ rowData: [], rowCount: 0 });
        return;
      }

      const { request, api } = params;
      const startRow = request.startRow ?? 0;
      const endRow = request.endRow ?? startRow + 50;
      const take = Math.max(1, endRow - startRow);

      const filterModel = (request.filterModel ?? {}) as Record<
        string,
        FilterModel
      >;
      const operationType = pickSetFilterFirstValue(filterModel.operationType);
      const userEmail = pickTextFilter(filterModel.userEmail);

      try {
        const response = await auditApi.fetchAuditHistory({
          objectId,
          skip: startRow,
          take,
          q: filtersRef.current.q || undefined,
          startDate: filtersRef.current.startDate || undefined,
          endDate: filtersRef.current.endDate || undefined,
          operationType: operationType || undefined,
          userEmail: userEmail || undefined,
        });

        if (onMetadata) onMetadata(response.entityDisplayName);

        const rowData: TransformedLog[] = response.items.map(transformAuditLog);
        const rowCount = response.hasMore
          ? undefined
          : startRow + rowData.length;

        params.success({ rowData, rowCount });

        if (startRow === 0 && rowData.length === 0 && api) {
          api.showNoRowsOverlay();
        } else if (rowData.length > 0 && api) {
          api.hideOverlay();
        }
      } catch (err) {
        console.error('Audit history fetch failed', err);
        params.fail();
      }
    },
    [objectId, onMetadata],
  );

  return useMemo<IServerSideDatasource>(() => ({ getRows }), [getRows]);
}

function pickSetFilterFirstValue(
  model: FilterModel | undefined,
): string | null {
  if (!model) return null;
  const m = model as { values?: unknown[] };
  if (Array.isArray(m.values) && m.values.length > 0) {
    return String(m.values[0] ?? '');
  }
  return null;
}

function pickTextFilter(model: FilterModel | undefined): string | null {
  if (!model) return null;
  const m = model as { type?: string; filter?: string };
  if (typeof m.filter === 'string' && m.filter.length > 0) {
    return m.filter;
  }
  return null;
}
